import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader2, Zap, Mail, Lock, User as UserIcon, Building2, Eye, EyeOff, Users, CheckCircle, Gift } from 'lucide-react';
import { analytics } from '../lib/ga4';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RegisterPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    company_name: '',
    partner_code: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [partnerValid, setPartnerValid] = useState(null);
  const [partnerName, setPartnerName] = useState('');
  const { register } = useAuth();
  const navigate = useNavigate();

  // Check for referral code in URL
  useEffect(() => {
    const refCode = searchParams.get('ref');
    if (refCode) {
      setFormData(prev => ({ ...prev, partner_code: refCode }));
      validatePartnerCode(refCode);
    }
  }, [searchParams]);

  const validatePartnerCode = async (code) => {
    if (!code || code.length < 4) {
      setPartnerValid(null);
      setPartnerName('');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/partners/validate-code/${code}`);
      const data = await res.json();
      setPartnerValid(data.valid);
      setPartnerName(data.valid ? data.partner_name : '');
    } catch {
      setPartnerValid(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // Validate partner code on change
    if (name === 'partner_code') {
      validatePartnerCode(value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await register(
        formData.email,
        formData.password,
        formData.full_name,
        formData.company_name,
        formData.partner_code
      );
      // Track successful signup in GA4
      analytics.trackSignup('email');
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 via-lime-50 to-white p-4 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-r from-emerald-400 to-lime-400 rounded-full blur-3xl opacity-10"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-r from-emerald-400 to-lime-400 rounded-full blur-3xl opacity-10"></div>
      
      <Card className="w-full max-w-md shadow-xl border-slate-200 relative z-10">
        <CardHeader className="space-y-1 text-center">
          <Link to="/" className="flex justify-center mb-4">
            <img src="/smartfacture-logo.png" alt="SmartFacture AI" className="h-12 w-auto" />
          </Link>
          <CardTitle className="text-2xl font-bold">{t('auth.register.title')}</CardTitle>
          <CardDescription className="text-base">
            {t('auth.register.subtitle')}
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <div className="space-y-2">
              <Label htmlFor="full_name" className="text-sm font-medium">{t('auth.register.firstName')}</Label>
              <div className="relative">
                <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="full_name"
                  name="full_name"
                  type="text"
                  placeholder="John Doe"
                  value={formData.full_name}
                  onChange={handleChange}
                  required
                  className="pl-10"
                  data-testid="register-fullname-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="company_name" className="text-sm font-medium">{t('auth.register.company')}</Label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="company_name"
                  name="company_name"
                  type="text"
                  placeholder="Your Company Ltd"
                  value={formData.company_name}
                  onChange={handleChange}
                  className="pl-10"
                  data-testid="register-company-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium">{t('auth.register.email')}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder={t('auth.login.emailPlaceholder')}
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="pl-10"
                  data-testid="register-email-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium">{t('auth.register.password')}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={handleChange}
                  required
                  minLength="6"
                  className="pl-10 pr-10"
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                  tabIndex={-1}
                  aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            
            {/* Partner Code Field */}
            <div className="space-y-2">
              <Label htmlFor="partner_code" className="text-sm font-medium flex items-center gap-2">
                <Users className="h-4 w-4 text-slate-400" />
                Code partenaire (optionnel)
              </Label>
              <div className="relative">
                <Input
                  id="partner_code"
                  name="partner_code"
                  type="text"
                  placeholder="Ex: CABI6AF0"
                  value={formData.partner_code}
                  onChange={handleChange}
                  className={`uppercase ${partnerValid === true ? 'border-emerald-500 bg-emerald-50' : partnerValid === false ? 'border-red-300' : ''}`}
                  data-testid="register-partner-code-input"
                />
                {partnerValid === true && (
                  <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-emerald-500" />
                )}
              </div>
              {partnerValid === true && partnerName && (
                <div className="flex items-center gap-2 text-sm text-emerald-600 bg-emerald-50 rounded-lg px-3 py-2">
                  <Gift className="h-4 w-4" />
                  <span>Référé par <strong>{partnerName}</strong> - 1 mois offert !</span>
                </div>
              )}
              {partnerValid === false && formData.partner_code.length >= 4 && (
                <p className="text-sm text-red-500">Code partenaire invalide</p>
              )}
            </div>
          </CardContent>
          <CardFooter className="flex flex-col space-y-4">
            <Button 
              type="submit" 
              className="w-full bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white" 
              disabled={loading}
              data-testid="register-submit-button"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('auth.register.creating')}
                </>
              ) : (
                t('auth.register.submitButton')
              )}
            </Button>
            <p className="text-sm text-center text-slate-600">
              {t('auth.register.haveAccount')}{' '}
              <Link to="/login" className="text-emerald-600 hover:text-emerald-700 font-medium hover:underline">
                {t('auth.register.signInLink')}
              </Link>
            </p>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
};

export default RegisterPage;
